import { createContext, useCallback, useEffect, useRef, useState } from "react";
import {
  FirestoreQueryState,
  TableContextValue,
  TableProviderProps,
} from "../models/Models";
import * as lc from "../../../modules/localstorage/index";
import { DocumentData, QueryDocumentSnapshot } from "firebase/firestore";
import useFirestoreData from "../hook/useFirestoreData";
import { minimalDefaultValue } from "./defaultValue";
import SocketConnection from "src/setup/socket/socket";
import { isEqual } from "lodash";

export const TableContext =
  createContext<TableContextValue<any>>(minimalDefaultValue);

async function defaultMapper<T>(
  docs: QueryDocumentSnapshot<T, DocumentData>[]
): Promise<T[]> {
  return docs.map((doc) => ({ ...doc.data(), id: doc.id } as T));
}

export function TableProvider<T>({
  listName,
  children,
  childCollection,
  collection,
  collectionGroup,
  columns,
  selection,
  filters,
  sorting,
  searching,
  extra,
  tabs,
  pagination,
  mapper = defaultMapper,
  useFilter,
  action,
  childColumns, // array of child column [array[0] array[1]
  subcollection,
  subSubcollection,
  childIndex, //numberƒƒ
}: TableProviderProps<T>) {
  const clientID = lc.getItemLC(lc.LCName.Client)?.id;
  const userID = lc.getItemLC(lc.LCName.UserID);
  const [TableState, setTableState] = useState<FirestoreQueryState<T>>({
    listName: listName,
    collection: collection,
    clientID: clientID,
    filters:
      tabs && tabs?.length > 0 ? tabs[0].filter : filters ? filters : undefined,
    sorting: tabs && tabs?.length > 0 ? tabs[0].sort : sorting,
    limit: pagination ? pagination[0] : 10,
    currentPage: 1,
    searchKey: searching,
    collectionGroup: collectionGroup,
    tabActive: 0,
    useFilter: useFilter,
    action,
    priorityOrder: true,
    subcollection: subcollection,
    subSubcollection: subSubcollection,
  } as FirestoreQueryState<T>);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const { data, isFetching, isLoading, refetch } =
    useFirestoreData<T>(TableState);

  const updateTableState = (newData: Partial<FirestoreQueryState<T>>) => {
    let newState = newData;

    if (!newData.currentPage) {
      newState.currentPage = 1;
    }

    if (
      isEqual(TableState, {
        ...TableState,
        ...newData,
      })
    ) {
      return;
    }
    setTableState((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  const updatePage = (newPage: number) => {
    updateTableState({
      currentPage: newPage,
    });
    setSelectedItems([]);
  };

  const refreshData = useCallback(async () => {
    let filter_data = tabs ? tabs[TableState.tabActive].filter : filters;
    let sorting_data = tabs ? tabs[TableState.tabActive].sort : sorting;

    updateTableState({
      useFilter: useFilter,
      filters: filter_data
        .filter((x: any) => x.label !== "end-search")
        .filter((x: any) => x.label !== "start-search"),
      sorting: sorting_data.filter((x: any) => x.label !== "search"),
      currentPage: 1,
      limit: pagination ? pagination[0] : 10,
    });

    setSelectedItems([]);
    refetch();
  }, [
    tabs,
    filters,
    sorting,
    TableState.limit,
    TableState.tabActive,
    useFilter,
    refetch,
  ]);

  useEffect(() => {
    const socket = SocketConnection();
    const onDataChange = () => refreshData();
    socket.on(`${collection}-data-update`, onDataChange);

    return () => {
      socket.off(`${collection}-data-update`, onDataChange);
    };
  }, [collection, refreshData]);

  const value = {
    TableState,
    selectedItems,
    setSelectedItems,
    updateTableState,
    updatePage,
    isLoading,
    isFetching,
    refreshData,
    data,
  };

  return (
    <TableContext.Provider value={value as any}>
      {children}
    </TableContext.Provider>
  );
}
